import { inject, injectable } from 'inversify';
import { makeAutoObservable } from 'mobx';
import { IOsiFlatOsvWidgetViewModel } from '@shared/types/mobx/widgets/OsiFlatOsvWidget';
import api from '@app/api';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { makeFlat } from '@shared/utils/helpers/makeFlat';
import { getOsiAbonents } from '@shared/api/osi/abonents/get';
import { Abonent } from '@shared/types/osi/abonents';
import { getOsvRowsByFlat } from '@shared/utils/helpers/osv';
import { SettingsStore } from '@mobx/services/SettingsStore';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { AreaTypeCodes } from '@shared/types/dictionaries';
// Утилиты схлопывания дочерних помещений: определение дочернего типа и слияние ОСВ по периодам
import { isChildAreaType, mergeFlatOsvByPeriod } from '@shared/utils/helpers/collapseChildAbonents';
import i18next from 'i18next';
import { MIME, downloadBase64 } from '@shared/utils/download';

@injectable()
export class OsiFlatOsvWidgetViewModel implements IOsiFlatOsvWidgetViewModel {
  abonents: Abonent[] = [];

  isLoading = false;

  periodsNumber = 0;

  rows: any[] = [];

  selectedAbonent: Abonent | null = null;

  totalsOsv: any[] = [];

  // Флаг «Свернуть дочерние помещения»: скрывает дочерние помещения из выбора
  // и вливает их суммы в родителя при построении ОСВ.
  // По умолчанию ВКЛЮЧЁН (дочерние помещения свёрнуты, паркинги/кладовки влиты в родителя):
  // class field инициализируется до тела конструктора, поэтому первичный loadOsv (через useEffect
  // после установки selectedAbonent) уже строит ОСВ в свёрнутом виде, а чекбокс отмечен.
  isCollapseChildren = true;

  // --- Кэш сырых данных ОСВ для локальной пересборки без сети ---
  // id абонента, для которого сейчас лежат данные в кэше. По смене абонента кэш считается
  // невалидным (id не совпадает) и loadOsv грузит данные заново.
  private _cachedAbonentId: number | null = null;

  // Сырой ответ api.getOsvOnAllPeriods для выбранного помещения (родителя) — кэш.
  private _rawParentOsv: any = null;

  // Сырые ответы api.getOsvOnAllPeriods для дочерних помещений выбранного родителя — кэш.
  // Загружаются один раз вместе с родителем и переиспользуются при переключении флага.
  private _rawChildrenOsv: any[] = [];

  private _handleLangChange = () => {
    this.recomputeAbonentFlats();
  };

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService, private settingsStore: SettingsStore) {
    makeAutoObservable(this);
    this.loadAbonents();
    i18next.on('languageChanged', this._handleLangChange);
  }

  dispose = () => {
    i18next.off('languageChanged', this._handleLangChange);
  };

  private recomputeAbonentFlats = () => {
    if (!this.abonents?.length) return;
    this.abonents = this.abonents.map((a: any) => {
      // ARENDATOR: flat = flatDisplay (единый номер от backend), от языка не зависит — не пересчитываем
      if (a.areaTypeCode === AreaTypeCodes.ARENDATOR) return a;
      const raw = a.flatRaw ?? a.flat;
      return { ...a, flatRaw: raw, flat: makeFlat({ ...a, flat: raw }) };
    });
    // selectedAbonent тоже надо обновить чтобы Autocomplete отрисовал новый префикс
    if (this.selectedAbonent) {
      const updated = this.abonents.find((a: any) => a.id === this.selectedAbonent?.id);
      if (updated) this.selectedAbonent = updated;
    }
  };

  // Список помещений для выпадающего списка выбора.
  // При выключенном флаге — все абоненты; при включённом — без дочерних помещений
  // (паркинги/кладовки с привязкой к родителю), чтобы их нельзя было выбрать отдельно.
  get visibleAbonents(): Abonent[] {
    if (!this.isCollapseChildren) return this.abonents;
    return this.abonents.filter(
      (a: any) => !(isChildAreaType(a.areaTypeCode) && a.parentAbonentId != null)
    );
  }

  // OSI-806: признак наличия у ОСИ дочерних помещений (паркинги/кладовки).
  // Источник правды — общий MobX-сервис OsiService (osiInfo.hasChildAbonents), критерий считает бэк
  // (активный абонент с parentAbonentId != null и areaTypeCode ∈ {PARKING, STORAGE}).
  // Нужен UI, чтобы НЕ показывать чекбокс «Свернуть дочерние помещения» в домах, где сворачивать нечего.
  // ВАЖНО: сам флаг isCollapseChildren при скрытом чекбоксе НАМЕРЕННО НЕ сбрасываем (остаётся true) —
  // дочерних помещений нет, поэтому ни локальная свёртка (mergeFlatOsvByPeriod), ни query-параметр
  // collapseChildren в выгрузке ОСВ по помещению ничего не меняют.
  get hasChildAbonents(): boolean {
    return this.osiModule.osiInfo?.hasChildAbonents ?? false;
  }

  // Переключатель флага «Свернуть дочерние помещения».
  // ГЛУБОКИЙ ФИКС РЕГРЕССИИ: переключение флага — ЛОКАЛЬНЫЙ пересчёт из кэша,
  // БЕЗ сетевого запроса и БЕЗ поднятия isLoading (чтобы не было моргания/размонтирования виджета).
  setCollapseChildren = (value: boolean): void => {
    this.isCollapseChildren = value;

    // Краевой случай: был выбран дочерний абонент (паркинг/кладовка), включили сворачивание —
    // ребёнок исчезает из visibleAbonents, поэтому переключаемся на его родителя.
    if (
      value &&
      this.selectedAbonent &&
      isChildAreaType(this.selectedAbonent.areaTypeCode) &&
      this.selectedAbonent.parentAbonentId != null
    ) {
      const parent = this.abonents.find((a) => a.id === this.selectedAbonent!.parentAbonentId);
      if (parent) this.selectedAbonent = parent;
      // selectedAbonent сменился → useEffect в UI сам вызовет loadOsv() для родителя.
      // НЕ дёргаем loadOsv здесь, чтобы не было ДВОЙНОЙ загрузки. Тихая догрузка пойдёт через
      // неблокирующий overlay грида (виджет не размонтируется). Если данные родителя уже были
      // в кэше — проверка ниже пересоберёт их локально без сети.
    }

    // ОСНОВНОЙ КЕЙС (выбран родитель): данные текущего абонента лежат в кэше — пересобираем
    // представление ЛОКАЛЬНО (mergeFlatOsvByPeriod над кэшем при true, либо чистый родитель при false).
    // Сеть НЕ дёргаем, isLoading НЕ поднимаем — ни моргания, ни размонтирования виджета.
    if (this.selectedAbonent?.id != null && this._cachedAbonentId === this.selectedAbonent.id) {
      this.rebuildRows();
    }
    // Иначе (кэш-промах после переключения на родителя) — загрузку инициирует useEffect по смене selectedAbonent.
  };

  // Пересборка строк грида из кэша сырых ОСВ (родитель + дети) с учётом флага схлопывания.
  // Без сетевого запроса: при включённом флаге услуги детей вливаются в родителя по периодам.
  // Бизнес-логику слияния (mergeFlatOsvByPeriod) не меняем — меняем только ИСТОЧНИК (кэш вместо сети).
  private rebuildRows = (): void => {
    if (this._rawParentOsv == null) {
      // Данных нет — очищаем грид
      this.rows = [];
      this.totalsOsv = [];
      this.periodsNumber = 0;
      return;
    }

    // По умолчанию — только родитель; при включённом флаге сливаем кэшированных детей
    let merged = this._rawParentOsv;
    if (this.isCollapseChildren && this._rawChildrenOsv.length) {
      merged = mergeFlatOsvByPeriod(this._rawParentOsv, this._rawChildrenOsv);
    }

    const [rows, total] = getOsvRowsByFlat(merged, this.settingsStore.moment);
    this.periodsNumber = merged.length;
    this.rows = rows;
    this.totalsOsv = total;
  };

  downloadOsv = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId || !this.selectedAbonent?.id) throw new Error('osiId or abonentId is undefined');

      this.isLoading = true;

      const data = await api.getAbonentOSVReport(
        this.osiModule.osiId,
        this.selectedAbonent.id,
        // Фаза 2: в отчёт/oci-docs передаём СЫРОЙ номер помещения (flatRaw), не отображаемый flat
        (this.selectedAbonent as any)?.flatRaw ?? this.selectedAbonent?.flat,
        // Передаём флаг сворачивания дочерних помещений для oci-docs
        this.isCollapseChildren
      );
      const osiName = this.osiModule.osiInfo?.name.startsWith(this.osiModule.osiInfo?.unionTypeRu ?? '')
        ? this.osiModule.osiInfo?.name
        : `${this.osiModule.osiInfo?.unionTypeRu} ${this.osiModule.osiInfo?.name}`;
      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64.
      // Фаза 2: имя файла — по СЫРОМУ номеру помещения (flatRaw)
      downloadBase64(
        data as unknown as string,
        MIME.XLSX,
        `Оборотно_сальдовая_ведомость_${osiName}_по_помещению_${
          (this.selectedAbonent as any)?.flatRaw ?? this.selectedAbonent?.flat
        }.xlsx`
      );
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  loadAbonents = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');

      this.isLoading = true;
      const data = await getOsiAbonents(this.osiModule.osiId);

      // Фаза 2: НЕ мутируем flat. flatRaw хранит СЫРОЙ номер помещения (для запроса ОСВ и имени файла),
      // flat — отображаемое значение по ГЛАВНОМУ ПРАВИЛУ: арендатор → flatDisplay (единый номер
      // от backend «Аренда(...)»), иначе → makeFlat (префикс типа помещения Неж-/Парк-/Клад-).
      this.abonents = data.map((e: any) => {
        const raw = e.flat;
        const display =
          e.areaTypeCode === AreaTypeCodes.ARENDATOR ? (e.flatDisplay ?? raw) : makeFlat({ ...e, flat: raw });
        return { ...e, flatRaw: raw, flat: display };
      });
      this.selectedAbonent = data?.length ? data[0] : null;
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  loadOsv = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId || !this.selectedAbonent?.id) throw new Error('osiId or abonentId is undefined');

      this.isLoading = true;

      const abonentId = this.selectedAbonent.id;

      // Сырой ответ ОСВ выбранного помещения (родителя)
      const data = await api.getOsvOnAllPeriods(abonentId);

      // Дети выбранного абонента — дочерние помещения (паркинги/кладовки), привязанные к нему.
      // Грузим их ВСЕГДА (независимо от флага), чтобы дальнейшее переключение чекбокса
      // пересобиралось локально из кэша без новых сетевых запросов в обе стороны.
      const children = this.abonents.filter(
        (a: any) => a.parentAbonentId === abonentId && isChildAreaType(a.areaTypeCode)
      );
      const childrenData = children.length
        ? await Promise.all(children.map((c) => api.getOsvOnAllPeriods(c.id)))
        : [];

      // Кэшируем сырые данные и помечаем, какому абоненту они принадлежат (для инвалидации по смене абонента)
      this._rawParentOsv = data;
      this._rawChildrenOsv = childrenData;
      this._cachedAbonentId = abonentId;

      // Строки строим централизованно из кэша с учётом флага (без повторной сети)
      this.rebuildRows();
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };
}
